import { Box, Paper, Chip, Avatar, Typography } from "@mui/material";
import clsx from "clsx";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from 'tss-react/mui';
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import socket from "../../hooks/useSocket";
import api from "../../services/api";
import apiUnitech from "../../services/apiUnidesk";
import ToastError from "../../toast/error/toastError";
import AutoCompleteTags from "../AutoCompleteTags";
import ContactDrawer from "../ContactDrawer";
import RequireContractModal from "../RequireContractModal";
import DragAndDropFiles from "../DragAndDropFiles";
import MessageInput from "../MessageInput/";
import MessagesList from "../MessagesList";
import TicketActionButtons from "../TicketActionButtons";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";

const drawerWidth = 260;

function extrairUUIDDaURL() {
  const url = window.location.href;
  const regex = /\/tickets\/([a-f\d-]+)$/i;
  const match = url.match(regex);

  if (match) {
    return match[1];
  } else {
    return null; // Retorna null se o UUID não for encontrado na URL.
  }
}

const getTicketStorage = () => {
  const ticket_id = localStorage.getItem("ticket_accept");
  localStorage.removeItem("ticket_accept");
  return ticket_id;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    '@media (max-width: 600px)': {
      borderRadius: '5px',
    },
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  ticketInfoContainer: {
    maxWidth: "100%",
    flexBasis: "100%",
    display: "flex",
    justifyContent: 'space-between !important',
    alignItems: 'center',
    alignContent: 'center',
    '@media (max-width: 600px)': {
      maxWidth: "95%",
      flexBasis: "95%",
    },
  },
  mainWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  mainWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  sharedUsersContainer: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    minHeight: '45px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '3px',
    backgroundColor: theme.palette.background.paper,
  },
  sharedUsersLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    minWidth: 'fit-content',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    flex: 1,
  }
}));

const Ticket = () => {
  const ticketId = extrairUUIDDaURL();
  const history = useHistory();
  const { classes } = useStyles();
  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [shared, setShared] = useState({});
  const [gestorData, setGestorData] = useState(null);
  const [ticket, setTicket] = useState({});
  const [tags, setTags] = useState([]);
  const [requireContractOpen, setRequireContractOpen] = useState(false);
  const [contractDismissedFor, setContractDismissedFor] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const reloadContentTicket = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticket.id}`);
        if (isCancelled) return;

        if (data.status !== 'pending') {
          setLoading(true);
          setTicket(data);
          setTimeout(() => {
            if (!isCancelled) setLoading(false);
          }, 2000);
        } else {
          intervalRef.current = setTimeout(reloadContentTicket, 2000);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error(err);
          setLoading(false);
        }
      } finally {
        setLoading(false);
      }
    }

    if (ticket?.id && ticket.status === 'pending') {
      reloadContentTicket();
    }

    return () => {
      isCancelled = true;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [ticket]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get(`/tickets/u/${ticketId}`);
        const { queueId } = data;
        const { queues, profile } = user;

        const queueAllowed = queues.find(q => q.id === queueId)
        if (!queueAllowed && !['user', 'admin', 'supervisor'].includes(profile)) {
          ToastError('Acesso não permitido');
          console.debug("Puxando pagina ticket na linha: 142")
          history.push("/tickets");
          return;
        }

        setContact(data.contact);
        setTicket(data);

        if (data?.isSharedGroup) {
          const { data: sharedData } = await api.get(`/ticket/${data.id}/share-group`);
          setShared(sharedData?.users || []);
        };

      } catch (err) {
        ToastError(err);
        console.debug("Puxando pagina ticket na linha: 153")
        history.push("/tickets");
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    if (+user?.companyId === 2) {
      setGestorData(null);
      handleGestorData(contact);
    }
  }, [contact, user])

  const handleGestorData = async (ticketContact) => {
    if (ticketContact?.contract) {
      try {
        const { data: gestor } = await apiUnitech.get(`/api/gerenciamentounitech/${ticketContact.contract}?api_key=gestor_unicheff_ee046272_6f38_4f51_9d12_ef8152a228e1`)
        setGestorData(gestor);
      } catch (err) {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    if (!user?.companyId) return;

    const userCompany = user.companyId;

    const handleSocketConnect = () => socket.emit("joinChatBox", `${ticket.id}`);

    const handleTicketUpdate = async (data) => {

      if (!ticket) return;

      if (data.action === "update" && (data?.ticket?.uuid === extrairUUIDDaURL())) {
        setTicket(data.ticket);
      }

      if (data.action === "delete" && (data?.ticket?.uuid === extrairUUIDDaURL())) {
        const ticket_accept_id = getTicketStorage();
        console.debug(`atual: ${ticket_accept_id}, socket: ${data?.ticketId}`);
        console.debug(`uuid:${data?.ticket?.uuid}, url: ${extrairUUIDDaURL()}`);
        if (ticket_accept_id && (+ticket_accept_id !== +data?.ticketId)) {
          console.debug("Puxando pagina ticket na linha: 177")
          history.push("/tickets");
        }
      }
    };

    const handleContactUpdate = (data) => {
      if (data.action === "update") {
        setContact((prevState) => (prevState.id === data.contact?.id) ? { ...prevState, ...data.contact } : prevState);
      }
    };

    // Handler nomeado para que possamos removê-lo no cleanup.
    // Antes o handler era uma arrow inline, sem referência guardada,
    // então não havia como dar off — vazava em cada execução do effect.
    const handleTicketShared = (data) => {
      if (!!data?.users) { setShared(data.users); }
    };

    socket.on("connect", handleSocketConnect);
    socket.on(`ticket-${userCompany}`, handleTicketUpdate);
    socket.on(`ticket-${userCompany}-shared`, handleTicketShared);
    socket.on("contact", handleContactUpdate);

    // Cleanup precisa do (event, callback) — chamar socket.off só com o
    // nome do evento NÃO removia nada (o off interno faz indexOf(undefined)).
    return () => {
     // socket.off("connect", handleSocketConnect);
    //  socket.off(`ticket-${userCompany}`, handleTicketUpdate);
    //  socket.off(`ticket-${userCompany}-shared`, handleTicketShared);
    //  socket.off("contact", handleContactUpdate);
    };

  }, [ticketId, ticket, history, user]);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handleChangeTagSelect = async tagIds => {
    if (!contact?.id) return;
    try {
      const { data } = await api.put(`/contacts/${contact.id}`, { ...contact, tagIds: tagIds });
      setTags(data.tags.map(tag => tag.id));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const selectedTagsIds = Array.isArray(ticket?.contact?.tags) ? ticket.contact.tags.map(tag => tag.id) : [];
    setTags(selectedTagsIds);
  }, [ticket]);

  useEffect(() => {
    if (+user?.companyId !== 2) return;
    if (!contact?.id) return;
    if (ticket?.status !== 'open') return;
    if (contractDismissedFor === contact.id) return;
    if (contact.contract && String(contact.contract).trim()) return;

    let cancelled = false;
    api
      .get(`/contacts/${contact.id}`)
      .then(({ data }) => {
        if (cancelled || !data) return;
        if (data.contract && String(data.contract).trim()) {
          setContact((prev) => (prev?.id === data.id ? { ...prev, contract: data.contract } : prev));
        } else {
          setRequireContractOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setRequireContractOpen(true);
      });

    return () => { cancelled = true; };
  }, [contact?.id, ticket?.status, user?.companyId, contractDismissedFor]);

  const handleContractSaved = (updatedContact) => {
    setContact((prev) => ({ ...prev, ...updatedContact, contract: updatedContact?.contract }));
    setRequireContractOpen(false);
  };

  const handleContractRemindLater = () => {
    if (contact?.id) setContractDismissedFor(contact.id);
    setRequireContractOpen(false);
  };

  return (
    <DragAndDropFiles className={classes.root} id="drawer-container" style={{ height: 'calc(100vh - 80px)', width: '100%' }}>
      <Paper variant="outlined" elevation={0} className={clsx(classes.mainWrapper, { [classes.mainWrapperShift]: drawerOpen })}>
        <TicketHeader loading={loading}>
          <Box className={classes.ticketInfoContainer}>
            <TicketInfo contact={contact} ticket={ticket} gestor={gestorData} onClick={handleDrawerOpen} />
            <TicketActionButtons ticket={ticket} contact={contact} gestor={gestorData} />
          </Box>
        </TicketHeader>
        <AutoCompleteTags
          selected={tags}
          name="tagIds"
          type="contacts"
          onSelect={handleChangeTagSelect}
        />

        {/* Campo de usuários compartilhados */}
        {Array.isArray(shared) && shared.length > 0 && (
          <Box className={classes.sharedUsersContainer}>
            <Typography className={classes.sharedUsersLabel}>
              Compartilhado com:
            </Typography>
            <Box className={classes.chipContainer}>
              {shared.map((user) => (
                <Chip
                  key={user.id}
                  size="small"
                  avatar={
                    <Avatar
                      src={user?.media}
                      alt={user.name}
                      sx={{ width: 24, height: 24 }}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  label={user.name}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <ReplyMessageProvider>
          <MessagesList ticket={ticket} ticketId={ticket.id} isGroup={ticket.isGroup} />
          <MessageInput id="inputMessage" ticketId={ticket.id} ticketStatus={ticket.status} style={{ marginLeft: '50px' }} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer open={drawerOpen} handleDrawerClose={handleDrawerClose} contact={contact} loading={loading} />
      <RequireContractModal
        open={requireContractOpen}
        contact={contact}
        onSaved={handleContractSaved}
        onRemindLater={handleContractRemindLater}
      />
    </DragAndDropFiles>
  );
};

export default Ticket;